import axios from "axios";
import { notify } from "../utils/notify";

// Create an Axios instance
const api = axios.create({
  baseURL: "/smartlocker", // Proxy handles forwarding to http://localhost:8079
  timeout: 15000,
});

let isRedirecting = false;

export function handleInstantLogout(customMsg = "Your session has expired. Please sign in again.") {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (path === '/login' || path.includes('/pages/login') || isRedirecting) {
    return;
  }
  isRedirecting = true;
  try {
    sessionStorage.setItem("session_logout_msg", customMsg);
  } catch (e) {}
  localStorage.removeItem("token");
  localStorage.removeItem("userInfo");
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("userInfo");
  // Instant clean navigation to login
  window.location.replace("/login");
}

// Helper to get role header
const getRoleHeader = () => {
  try {
    const raw = localStorage.getItem("userInfo") || sessionStorage.getItem("userInfo") || localStorage.getItem("user") || sessionStorage.getItem("user") || "{}";
    const user = JSON.parse(raw);
    if (user.role === 1 || String(user.role).toLowerCase() === 'super admin' || user.roleName === 'Super Admin' || user.name === 'admin' || user.username === 'admin') {
      return "Super Admin";
    }
  } catch (e) {}
  return "";
};

// Request interceptor to attach token & role
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (token) {
    config.headers["authorization"] = token;
    config.headers["Authorization"] = token;
    config.headers["token"] = token;
  }
  const role = getRoleHeader();
  if (role) {
    config.headers["X-Admin-Role"] = role;
  }
  return config;
});

// Global axios interceptor for /api2fa/ calls
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (token) {
    config.headers["authorization"] = token;
    config.headers["Authorization"] = token;
    config.headers["token"] = token;
  }
  const role = getRoleHeader();
  if (role) {
    config.headers["X-Admin-Role"] = role;
  }
  return config;
});

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Check if the backend indicates invalid or expired session
    if (response.data && response.data.code === 1) {
      const msg = (response.data.msg || "").toLowerCase();
      if (
        msg.includes("invalid login credentials") ||
        msg.includes("token expired") ||
        msg.includes("login timeout") ||
        msg.includes("user does not exist") ||
        msg.includes("please login again")
      ) {
        // Do not redirect if we are actively trying to login
        if (response.config.url && response.config.url.includes('/admin/login')) {
          return response;
        }
        handleInstantLogout();
        return Promise.reject(new Error(response.data.msg || "Session expired"));
      }
    }
    return response;
  },
  (error) => {
    if (error.response) {
      const status = error.response.status;
      const url = error.config?.url || "";
      const dataStr = typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data || {});
      
      // If 401/403 unauthorized on any protected endpoint
      if (status === 401 || status === 403 || (status === 400 && dataStr.includes("missing request header 'authorization'"))) {
        if (!url.includes('/login') && !url.includes('/2fa')) {
          handleInstantLogout();
        }
      }
    }
    return Promise.reject(error);
  }
);

// Automatic Session Keep-Alive & Revocation Monitor (checks every 10 seconds)
if (typeof window !== 'undefined') {
  setInterval(async () => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (token && window.location.pathname !== '/login' && !window.location.pathname.includes('/pages/login')) {
      try {
        const sessionTime = localStorage.getItem("session_time") || 0;
        const res = await axios.get('/api2fa/system/session-validity', {
          params: { sessionTime },
          timeout: 4000
        });
        if (res.data && res.data.valid === false) {
          handleInstantLogout(res.data.reason || "Two-Factor Authentication configuration changed. All active sessions have been terminated.");
        }
      } catch (_) {}
    }
  }, 10 * 1000);

  // Background Java ping (every 3 minutes)
  setInterval(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (token && window.location.pathname !== '/login' && !window.location.pathname.includes('/pages/login')) {
      api.get('/admin/v1/admin/view', { params: { id: 1 } }).catch(() => {});
    }
  }, 3 * 60 * 1000);
}

// ─── PASSWORD POLICY ENFORCEMENT ─────────────────────────────────────────────
export const STRONG_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
export const PASSWORD_REQUIREMENT_MSG = "Password must be at least 8 characters long and contain at least 1 uppercase letter, 1 lowercase letter, and 1 number.";

export const validatePasswordComplexity = (pwd) => {
  if (pwd && !STRONG_PASSWORD_REGEX.test(pwd)) {
    throw new Error(PASSWORD_REQUIREMENT_MSG);
  }
};

// ─── AUTHENTICATION ─────────────────────────────────────────────────────────
export const adminLogin = async (name, pwd) => {
  const res = await api.post("/admin/v1/admin/login", null, { params: { name, pwd } });
  
  // If user has 2FA enabled in DB, Java returns { requires2FA: true, preAuthToken: "..." }
  // Check if system-wide 2FA is currently paused!
  if (res.data && res.data.value && res.data.value.requires2FA && res.data.value.preAuthToken) {
    try {
      const statusRes = await axios.get("/api2fa/system/2fa-control");
      if (statusRes.data && statusRes.data.data && statusRes.data.data.isPaused) {
        // System 2FA is paused! Auto-complete login without user 2FA prompt
        const compRes = await api.post("/admin/v1/admin/complete2FA", null, {
          params: { preAuthToken: res.data.value.preAuthToken }
        });
        if (compRes.data && (compRes.data.code === 0 || compRes.data.code === 200)) {
          return compRes;
        }
      }
    } catch (_) {}
  }
  return res;
};

export const updatePwd = (oldPwd, newPwd) => {
  validatePasswordComplexity(newPwd);
  return api.post("/admin/v1/admin/updatePwd", null, { params: { oldPwd, newPwd } });
};

export const verifyPassword = (password) => {
  return api.post("/admin/v1/admin/verifyPassword", { password });
};

// ─── 2FA SYSTEM-WIDE MASTER CONTROL ──────────────────────────────────────────
export const get2FAControlStatus = async () => {
  const res = await axios.get("/api2fa/system/2fa-control");
  return res.data?.data || res.data;
};

export const pause2FA = async (hours, reason, forceLogout = true) => {
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const adminName = userInfo.name || userInfo.username || "Super Admin";
  const res = await axios.post("/api2fa/system/2fa-pause", {
    hours,
    reason,
    forceLogout,
    adminName
  });
  return res.data;
};

export const resume2FA = async () => {
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const adminName = userInfo.name || userInfo.username || "Super Admin";
  const res = await axios.post("/api2fa/system/2fa-resume", { adminName });
  return res.data;
};

export const forceLogoutAll = async (reason) => {
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const adminName = userInfo.name || userInfo.username || "Super Admin";
  const res = await axios.post("/api2fa/system/force-logout-all", { reason, adminName });
  return res.data;
};

// ─── HELPER FOR CRUD ────────────────────────────────────────────────────────
const generateCrud = (basePath) => ({
  getAll: (params) => api.get(`${basePath}/findAll`, { params }),
  getOne: (id) => api.get(`${basePath}/view`, { params: { id } }),
  add: (data) => api.post(`${basePath}/save`, null, { params: data }),
  update: (data) => api.post(`${basePath}/update`, null, { params: data }),
  remove: (id) => api.post(`${basePath}/remove`, null, { params: { id } }),
  updateStatus: (id, status) => api.post(`${basePath}/updateStatus`, null, { params: { id, status } })
});

// ─── ENTITIES ───────────────────────────────────────────────────────────────

export const adminApi = {
  getAll: (params) => axios.get("/api2fa/api/admin/findAll", { params }),
  getOne: (id) => axios.get("/api2fa/api/admin/view", { params: { id } }),
  add: (data) => {
    if (data?.pwd) validatePasswordComplexity(data.pwd);
    return axios.post("/api2fa/api/admin/save", data);
  },
  update: (data) => {
    if (data?.pwd) validatePasswordComplexity(data.pwd);
    return axios.post("/api2fa/api/admin/update", data);
  },
  remove: (id) => axios.post("/api2fa/api/admin/remove", { id }),
  updateStatus: (id, status) => axios.post("/api2fa/api/admin/updateStatus", { id, status }),
  getStats: () => axios.get("/api2fa/api/admin/stats"),
  getProfile: () => api.get("/admin/v1/admin/currentProfile"),
  updateProfile: (data) => axios.post("/api2fa/api/admin/update", data),
  updateAvatar: (avatar, id) => axios.post("/api2fa/api/admin/update", { avatar, id }),
  changePassword: (data) => {
    if (data?.newpassword) validatePasswordComplexity(data.newpassword);
    const params = new URLSearchParams();
    if (data.id) params.append("id", data.id);
    if (data.oldpassword) params.append("oldpassword", data.oldpassword);
    if (data.newpassword) params.append("newpassword", data.newpassword);
    if (data.newpassword2) params.append("newpassword2", data.newpassword2);
    if (data.oldpassword) params.append("oldPwd", data.oldpassword);
    if (data.newpassword) params.append("newPwd", data.newpassword);
    return api.post(`/admin/v1/admin/updatePwd?${params.toString()}`, params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  }
};
export const customerApi = {
  getAll: (params) => axios.get("/api2fa/api/customers/findAll", { params }),
  getOne: (id) => axios.get("/api2fa/api/customers/view", { params: { id } }),
  add: (data) => axios.post("/api2fa/api/customers/save", data),
  update: (data) => axios.post("/api2fa/api/customers/update", data),
  remove: (id) => axios.post("/api2fa/api/customers/remove", null, { params: { id } })
};
export const employeeApi = {
  ...generateCrud("/admin/v1/employee"),
  getAll: (params) => axios.get("/api2fa/api/employees/findAll", { params, headers: { Authorization: localStorage.getItem('token') || '' } }),
  getOne: (id) => axios.get("/api2fa/api/employees/view", { params: { id }, headers: { Authorization: localStorage.getItem('token') || '' } }),
  add: (data) => {
    if (data?.pwd) validatePasswordComplexity(data.pwd);
    const payload = { ...data };
    const isExternal = Number(payload.employeeType) === 1;
    payload.employeeType = isExternal ? 1 : 0;
    if (!isExternal) {
      payload.organization = "";
      payload.tradeName = "";
    } else {
      payload.organization = payload.organization || "";
      payload.tradeName = payload.tradeName || "";
    }
    return axios.post("/api2fa/api/employees/save", payload, {
      headers: { Authorization: localStorage.getItem('token') || '' }
    });
  },
  update: (data) => {
    if (data?.pwd) validatePasswordComplexity(data.pwd);
    const payload = { ...data };
    if (payload.employeeType !== undefined) {
      const isExternal = Number(payload.employeeType) === 1;
      payload.employeeType = isExternal ? 1 : 0;
      if (!isExternal) {
        payload.organization = "";
        payload.tradeName = "";
      } else {
        payload.organization = payload.organization || "";
        payload.tradeName = payload.tradeName || "";
      }
    }
    return axios.post("/api2fa/api/employees/update", payload);
  },
  requestVerification: (id, mobile) => api.post("/admin/v1/employee/requestVerification", { id, mobile }),
  verifyPhone: (id, code, mobile) => api.post("/admin/v1/employee/verifyPhone", { id, code, mobile })
};

// Devices & Boxes
export const deviceApi = { 
  ...generateCrud("/admin/v1/smartDevice"), 
  getAll: (params) => axios.get("/api2fa/api/devices/findAll", { params }),
  getOne: (id) => axios.get("/api2fa/api/devices/view", { params: { id } }),
  add: (data) => axios.post("/api2fa/api/devices/save", data),
  update: (data) => axios.post("/api2fa/api/devices/save", data),
  remove: (id) => axios.post("/api2fa/api/devices/remove", { id }),
  getStats: () => axios.get("/api2fa/api/devices/stats"),
  hardwareReset: (deviceNo, reason) => api.post("/admin/v1/device/hardwareReset", { deviceNo, reason }),
  setVolume: (deviceNo, volume) => api.post("/admin/v1/device/setVolume", { deviceNo, volume }),
  getVolume: (deviceNo) => api.get("/admin/v1/device/getVolume", { params: { deviceNo } }),
  getCurrency: (deviceNo) => axios.get(`/api2fa/api/devices/currency?deviceNo=${deviceNo}`),
  setCurrency: (deviceNo, currency) => axios.post("/api2fa/api/devices/set-currency", { deviceNo, currency })
};
export const adminDeviceApi = {
  getAll: (params) => axios.get("/api2fa/api/adminDevice/findAll", { params }),
  getOne: (id) => axios.get("/api2fa/api/adminDevice/view", { params: { id } }),
  add: (data) => axios.post("/api2fa/api/adminDevice/save", data),
  update: (data) => axios.post("/api2fa/api/adminDevice/save", data),
  remove: (id) => axios.post("/api2fa/api/adminDevice/remove", { id })
};

// Mock pickupCodeApi since the endpoint is missing on backend, preventing 404 console errors
export const pickupCodeApi = {
  getAll: () => Promise.resolve({ data: { code: 0, rows: [], total: 0 } }),
  add: () => Promise.resolve({ data: { code: 0 } }),
  update: () => Promise.resolve({ data: { code: 0 } }),
  remove: () => Promise.resolve({ data: { code: 0 } })
};
export const boxApi = { 
  ...generateCrud("/admin/v1/box"),
  openDoor: (id) => api.post("/admin/v1/box/openDoor", null, { params: { id } }),
  reset: (id) => api.post("/admin/v1/box/reset", null, { params: { id } }),
  batchSave: (data) => api.post("/admin/v1/box/batchSaveV1", null, { params: data }),
  bulkAction: (deviceNo, storageFilter, reason) => api.post("/admin/v1/box/bulkAction", { deviceNo, storageFilter, reason }),
  getBoxSizeConfig: () => api.get("/api/system/box-size-config").then(res => res.data?.data || res.data)
};

const generateCombinedHistoryCrud = (basePath, logType) => ({
  getAll: async (params) => {
    try {
      const pageIndex = Math.max(1, Number(params?.pageIndex || params?.page || 1));
      const pageSize = Math.max(1, Number(params?.pageSize || params?.limit || 10));

      const [backendRes, nodeRes] = await Promise.allSettled([
        api.get(basePath + "/findAll", { params: { ...params, _t: Date.now() } }),
        axios.get(`/api2fa/logs/${logType}`)
      ]);

      let backendList = [];
      if (backendRes.status === 'fulfilled' && backendRes.value?.data) {
        const val = backendRes.value.data.value || backendRes.value.data.data || {};
        backendList = val.list || (Array.isArray(val) ? val : []);
      }

      let nodeList = [];
      if (nodeRes.status === 'fulfilled' && Array.isArray(nodeRes.value?.data)) {
        nodeList = nodeRes.value.data;
      }

      const combined = [...nodeList, ...backendList];
      const totalRows = combined.length;
      const start = (pageIndex - 1) * pageSize;
      const sliced = combined.slice(start, start + pageSize);

      return {
        data: {
          code: 0,
          value: {
            list: sliced,
            totalRows: totalRows,
            rowTotal: totalRows,
            total: totalRows,
            totalCount: totalRows,
            pageIndex: pageIndex,
            pageSize: pageSize
          }
        }
      };
    } catch (e) {
      console.error(`Error fetching history for ${logType}`, e);
      return { data: { code: 0, value: { list: [], totalRows: 0, totalCount: 0 } } };
    }
  },
  remove: (id) => api.post(basePath + "/remove", null, { params: { id } })
});

export const hisEmailApi = generateCombinedHistoryCrud("/admin/v1/hisEmail", "email");
export const hisSmsApi = generateCombinedHistoryCrud("/admin/v1/hisSms", "sms");

export const hisRecordApi = generateCombinedHistoryCrud("/admin/v1/hisRecord", "record");
export const overtimeApi = generateCrud("/admin/v1/overtime");

// Orders
export const orderApi = {
  getAll: (params) => axios.get("/api2fa/api/orders/findAll", { params }),
  getOne: (id) => axios.get("/api2fa/api/orders/view", { params: { id } }),
  add: (data) => axios.post("/api2fa/api/orders/save", data),
  update: (data) => axios.post("/api2fa/api/orders/update", data),
  remove: (id) => axios.post("/api2fa/api/orders/remove", null, { params: { id } })
};

// Payments & Pricing
export const deliveryPaymentApi = {
  getAll: (params) => axios.get("/api2fa/api/delivery-payment/findAll", { params }),
  getOne: (id) => axios.get("/api2fa/api/delivery-payment/view", { params: { id } }),
  add: (data) => axios.post("/api2fa/api/delivery-payment/save", data),
  update: (data) => axios.post("/api2fa/api/delivery-payment/update", data),
  remove: (id) => axios.post("/api2fa/api/delivery-payment/remove", { id })
};

export const additionalPaymentApi = {
  getAll: (params) => axios.get("/api2fa/api/additional-payment/findAll", { params }),
  getOne: (id) => axios.get("/api2fa/api/additional-payment/view", { params: { id } }),
  add: (data) => axios.post("/api2fa/api/additional-payment/save", data),
  update: (data) => axios.post("/api2fa/api/additional-payment/update", data),
  remove: (id) => axios.post("/api2fa/api/additional-payment/remove", { id })
};

export const pickupPaymentApi = {
  getAll: (params) => axios.get("/api2fa/api/pickup-payment/findAll", { params }),
  getOne: (id) => axios.get("/api2fa/api/pickup-payment/view", { params: { id } }),
  add: (data) => axios.post("/api2fa/api/pickup-payment/save", data),
  update: (data) => axios.post("/api2fa/api/pickup-payment/update", data),
  remove: (id) => axios.post("/api2fa/api/pickup-payment/remove", { id })
};

export const deliveryPriceApi = {
  getAll: (params) => axios.get("/api2fa/api/delivery-price/findAll", { params }),
  getOne: (id) => axios.get("/api2fa/api/delivery-price/view", { params: { id } }),
  add: (data) => axios.post("/api2fa/api/delivery-price/save", data),
  update: (data) => axios.post("/api2fa/api/delivery-price/update", data),
  remove: (id) => axios.post("/api2fa/api/delivery-price/remove", { id })
};

export const pickupPriceApi = {
  getAll: (params) => axios.get("/api2fa/api/pickup-price/findAll", { params }),
  getOne: (id) => axios.get("/api2fa/api/pickup-price/view", { params: { id } }),
  add: (data) => axios.post("/api2fa/api/pickup-price/save", data),
  update: (data) => axios.post("/api2fa/api/pickup-price/update", data),
  remove: (id) => axios.post("/api2fa/api/pickup-price/remove", { id })
};

// Dynamic Payment & Overdue Pricing Engine
export const paymentConfigApi = {
  getSettings: () => axios.get("/api2fa/api/payment-config/settings"),
  updateSettings: (data) => axios.post("/api2fa/api/payment-config/settings", data),
  calculateQuote: (data) => axios.post("/api2fa/api/payment-config/calculate-quote", data),
  calculateOverdue: (data) => axios.post("/api2fa/api/payment-config/calculate-overdue", data)
};

// Geographic Map Zones & Courier Dispatch
export const zoneApi = {
  getAll: () => axios.get("/api2fa/api/zones/findAll"),
  add: (data) => axios.post("/api2fa/api/zones/save", data),
  update: (data) => axios.post("/api2fa/api/zones/update", data),
  remove: (id) => axios.post("/api2fa/api/zones/remove", { id })
};

export const courierZoneApi = {
  getAll: () => axios.get("/api2fa/api/courier-zones/findAll"),
  assign: (data) => axios.post("/api2fa/api/courier-zones/assign", data),
  remove: (data) => axios.post("/api2fa/api/courier-zones/remove", data)
};

export const autoDispatchApi = {
  dispatch: (orderNo, deviceNo) => axios.post("/api2fa/api/orders/auto-dispatch", { orderNo, deviceNo }),
  dispatchOrder: (orderNo, deviceNo) => axios.post("/api2fa/api/orders/auto-dispatch", { orderNo, deviceNo })
};

export const dispatchMonitorApi = {
  getStats: () => axios.get("/api2fa/api/dispatch/monitor/stats"),
  getActiveQueue: () => axios.get("/api2fa/api/dispatch/monitor/active"),
  getTimeline: (orderId) => axios.get(`/api2fa/api/dispatch/monitor/timeline/${orderId}`),
  redispatch: (data) => axios.post("/api2fa/api/dispatch/monitor/re-dispatch", data)
};

// API Configuration
export const apiParaApi = generateCrud("/admin/v1/apiPara");
export const apiValueApi = generateCrud("/admin/v1/apiValue");
export const apiDeviceApi = generateCrud("/admin/v1/apiDevice");
export const apkApi = {
  getAll: (params) => axios.get("/api2fa/api/apk/findAll", { params }),
  getOne: (id) => axios.get("/api2fa/api/apk/view", { params: { id } }),
  add: (data) => axios.post("/api2fa/api/apk/save", data),
  update: (data) => axios.post("/api2fa/api/apk/update", data),
  remove: (id) => axios.post("/api2fa/api/apk/remove", { id })
};

export const deviceGroupApi = {
  getAll: (params) => axios.get("/api2fa/api/device-group/findAll", { params }),
  add: (data) => axios.post("/api2fa/api/device-group/save", data),
  update: (data) => axios.post("/api2fa/api/device-group/update", data),
  remove: (id) => axios.post("/api2fa/api/device-group/remove", { id })
};

// Courier Organizations
export const courierOrgApi = {
  getAll: (params) => axios.get("/api2fa/api/courier-orgs/findAll", { params }),
  getOne: (id) => axios.get("/api2fa/api/courier-orgs/view", { params: { id } }),
  add: (data) => axios.post("/api2fa/api/courier-orgs/save", data),
  update: (data) => axios.post("/api2fa/api/courier-orgs/update", data),
  remove: (id) => axios.post("/api2fa/api/courier-orgs/remove", { id }),
};

// System Audit Logs
export const systemLogApi = {
  getAll: (params) => axios.get("/api2fa/api/audit-logs/findAll", { params }),
  getOne: (id) => axios.get("/api2fa/api/audit-logs/view", { params: { id } }),
  add: (data) => axios.post("/api2fa/api/audit-logs/save", data),
  remove: (id) => axios.post("/api2fa/api/audit-logs/remove", { id })
};

export const logAuditEvent = (action, module, details, status = "SUCCESS") => {
  try {
    const raw = localStorage.getItem("userInfo") || sessionStorage.getItem("userInfo") || localStorage.getItem("user") || sessionStorage.getItem("user") || "{}";
    const user = JSON.parse(raw);
    const actorName = user.name || user.username || user.account || user.email || (user.id ? `User #${user.id}` : "System");
    return axios.post("/api2fa/api/audit-logs/log", {
      adminId: user.id || user.userId || "",
      adminName: actorName,
      action,
      module,
      details,
      status
    }).catch(e => console.error("Audit log error:", e));
  } catch (e) {
    // silent fallback
  }
};

export const dashboardAnalyticsApi = {
  getAnalytics: () => axios.get("/api2fa/api/dashboard/analytics")
};

export const dashboardSummaryApi = {
  getSummaryStats: () => axios.get("/api2fa/api/dashboard/summary-stats"),
  getNetworkOverview: () => axios.get("/api2fa/api/dashboard/network-overview")
};

export const notificationApi = {
  getMatrix: () => axios.get("/api2fa/api/notifications/matrix"),
  updateMatrix: (matrix) => axios.post("/api2fa/api/notifications/matrix/update", { matrix })
};

// Linehaul & Logistics API
export const linehaulApi = {
  // Hubs
  getHubs: () => axios.get("/api2fa/api/linehaul/hubs"),
  addHub: (data) => axios.post("/api2fa/api/linehaul/hubs", data),
  updateHub: (id, data) => axios.put(`/api2fa/api/linehaul/hubs/${id}`, data),
  deleteHub: (id) => axios.delete(`/api2fa/api/linehaul/hubs/${id}`),
  getHubStaging: (hubId) => axios.get(`/api2fa/api/linehaul/hubs/${hubId}/staging`),

  // Vehicles
  getVehicles: () => axios.get("/api2fa/api/linehaul/vehicles"),
  addVehicle: (data) => axios.post("/api2fa/api/linehaul/vehicles", data),
  updateVehicleStatus: (id, status) => axios.put(`/api2fa/api/linehaul/vehicles/${id}/status`, { status }),

  // Routes & Drivers
  getRoutes: () => axios.get("/api2fa/api/linehaul/routes"),
  getDrivers: () => axios.get("/api2fa/api/linehaul/drivers"),

  // Transport Trips & Manifesting
  getTrips: () => axios.get("/api2fa/api/linehaul/trips"),
  createTrip: (data) => axios.post("/api2fa/api/linehaul/trips", data),
  getTripManifests: (tripId) => axios.get(`/api2fa/api/linehaul/trips/${tripId}/manifests`),
  manifestParcel: (tripId, data) => axios.post(`/api2fa/api/linehaul/trips/${tripId}/manifest`, data),
  reconcileTrip: (tripId, data) => axios.post(`/api2fa/api/linehaul/trips/${tripId}/reconcile`, data),
  departTrip: (tripId) => axios.post(`/api2fa/api/linehaul/trips/${tripId}/depart`),
  arriveTrip: (tripId) => axios.post(`/api2fa/api/linehaul/trips/${tripId}/arrive`),

  // Chain of Custody & Shipments
  getShipments: () => axios.get("/api2fa/api/linehaul/shipments"),
  getShipmentDetails: (orderNo) => axios.get(`/api2fa/api/linehaul/shipments/${orderNo}`),
  getHandovers: (orderNo) => axios.get(`/api2fa/api/linehaul/handovers/${orderNo}`),
  verifyHandover: (data) => axios.post("/api2fa/api/linehaul/handovers/verify", data)
};

// ─── DYNAMIC ENTERPRISE RBAC API ───────────────────────────────────────────
export const rbacApi = {
  getRoles: () => axios.get("/api2fa/api/rbac/roles"),
  getPermissions: () => axios.get("/api2fa/api/rbac/permissions"),
  getMatrix: () => axios.get("/api2fa/api/rbac/matrix"),
  getEffective: (roleId) => axios.get(`/api2fa/api/rbac/effective/${roleId}`),
  getMyPermissions: () => axios.get("/api2fa/api/rbac/my-permissions"),
  saveRolePermissions: (roleId, grantsMap, reason) => axios.post("/api2fa/api/rbac/role-permissions/save", { roleId, grantsMap, reason }),
  resetDefaults: () => axios.post("/api2fa/api/rbac/reset-defaults"),
  getAuditLogs: (params) => axios.get("/api2fa/api/rbac/audit-logs", { params })
};

export default api;

